import { DataTypes as DT } from 'business/definitions';
import { isDateOrTime } from 'helpers/chartHelper';

/**
 * Computes the number of different values in the response data for each column.
 * Adds it for each column as the value of the `valuesCount` key
 */
const addValuesCountToChartColumns = (
    chartColumns: ResponseColumnT[],
    data: ResponseDataT[],
): ResponseColumnT[] =>
    chartColumns.map((col) => {
        const differentValues = Array.from(
            new Set(data.map((d) => d[col.key])),
        );
        return { ...col, valuesCount: differentValues.length };
    });

/**
 * Sorts chart columns according to the number of different values the response data contains for each.
 * The `reverseGroupBy` setting parameter determines the order (descending if `true`, ascending otherwise).
 */
export const sortChartColumns = (
    chartColumns: ResponseColumnT[],
    chartData: ResponseDataT[],
    reverseGroupBy: boolean | undefined,
) => {
    const enhancedChartColumns = addValuesCountToChartColumns(
        chartColumns,
        chartData,
    );
    const sortFunction = (a: ResponseColumnT, b: ResponseColumnT) => {
        const aValue = a.valuesCount ?? 0;
        const bValue = b.valuesCount ?? 0;
        // The first two conditions to put the response columns with a single possible value at the end of the list
        if (aValue === 1) return 1;
        else if (bValue === 1) return -1;
        else {
            return reverseGroupBy
                ? aValue < bValue
                    ? 1
                    : -1
                : aValue < bValue
                  ? -1
                  : 1;
        }
    };
    return enhancedChartColumns.sort(sortFunction);
};

/**
 * When asked to find the most relevant column inside a sorted list of response columns,
 * we first prioritize date/time columns, then integer ones, then numeric ones, and finally any column.
 * This is a relicate of the old "magic-frontend".
 */
const findMostRelevantColumn = (columns: ResponseColumnT[]) => {
    const dateOrTimeExtraColumn = columns.find((col) => isDateOrTime(col.type));
    if (dateOrTimeExtraColumn) return dateOrTimeExtraColumn;
    const integerColumn = columns.find((col) => col.type === DT.integer);
    if (integerColumn) return integerColumn;
    const otherNumericExtraColumn = columns.find((col) =>
        [DT.float, DT.money].includes(col.type),
    );
    if (otherNumericExtraColumn) return otherNumericExtraColumn;
    return columns[0];
};

/**
 * Retrieves the first remaining column from the effective columns that has not been used,
 * or falls back to the best suitable column from other columns.
 */
export const getBestSuitableRemainingColumn = (
    sortedColumns: ResponseColumnT[],
    alreadyUsedKeys: string[],
): ResponseColumnT | undefined => {
    const remainingColumns = sortedColumns.filter(
        (col) => !alreadyUsedKeys.includes(col.key),
    );
    if (remainingColumns.length === 0) return undefined;

    // We first check if there is a remaining effective column that should be used
    const remainingEffectiveColumns = remainingColumns.filter(
        (col) => col.valuesCount && col.valuesCount > 1,
    );
    if (remainingEffectiveColumns.length > 0)
        return findMostRelevantColumn(remainingEffectiveColumns);

    // If all the effective columns have already been used, we look for an extra column in the whole list of remaining columns.
    return findMostRelevantColumn(remainingColumns);
};
